import React, { useState, useRef, useEffect } from "react";
import { useDraft, useLocalWizardDraft } from "@/lib/drafts";
import { goToCheckout } from "@/lib/checkout";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { WizardViews, WizardAuthStep } from "@/components/DeclareWizard";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { RecaptchaNotice } from "@/components/RecaptchaNotice";
import { fileToCompressedDataUrl } from "@/lib/image";
import PhotoEditor from "@/components/PhotoEditor";
import CategorySelect from "@/components/CategorySelect";
import { useB2C } from "@/context/B2CAuthContext";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/lib/images";
import { COUNTRIES, countryFlag, countryName, isAfrican } from "@/lib/countries";
import { CATEGORIES, labelFor } from "@/lib/categories";
import BoostAdDialog from "@/components/BoostAdDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { CountrySelect } from "@/components/CountrySelect";
import { PhoneInput } from "@/components/PhoneInput";
import { Sparkles, CheckCircle2, Upload, Eye, Lightbulb, Gift, MapPin, Facebook, MessageCircle, Link2, Download } from "lucide-react";
import { XIcon } from "@/components/XIcon";
import { InstagramIcon, TikTokIcon } from "@/components/BrandIcons";
import { ItemDetailFields, accountIdentity, MissingPhoneField, savePhoneIfMissing, phoneMissing, PublishedAs, EXTRA_DEFAULTS, extraPayload, usePrefillFromProfile } from "@/components/DeclarationFields";
import PublicationPlanPicker from "@/components/PublicationPlanPicker";
import { FormHeader, StepProgress } from "@/components/FormShell";
import SubmitOverlay from "@/components/SubmitOverlay";
import { ArrowLeft, ArrowRight, SearchX } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";
import { securePhoto } from "@/lib/photoSecurity";

const REWARD_SUGGEST = {
  Telephone: 50, Ordinateur: 50, Tablette: 50, Bijoux: 50,
  Portefeuille: 40, CarteBancaire: 40, Documents: 40, Montre: 40, Instrument: 40,
  Cles: 30, Sac: 30, Valise: 30, Electronique: 30, Animal: 30,
  Ecouteurs: 25, Lunettes: 25, USB: 20, Sport: 20, Medical: 20,
  Chargeur: 15, Livre: 10, Jouet: 10, Parapluie: 10,
};
const HIGH_COST_CITIES = ["paris", "lyon", "nice", "cannes", "monaco", "genève", "geneve",
  "zurich", "londres", "london", "dubai", "dubaï", "new york", "tokyo", "luxembourg",
  "bruxelles", "amsterdam", "dublin", "singapour", "hong kong", "oslo", "copenhague"];
const cityMultiplier = (place) => {
  const p = (place || "").toLowerCase();
  return HIGH_COST_CITIES.some((c) => p.includes(c)) ? 1.3 : 1.0;
};
const periodMultiplier = (dateStr) => {
  if (!dateStr) return 1.0;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 1.0;
  const m = d.getMonth(); const day = d.getDate();
  // Haute saison (vacances de fin d'année + été) → +15 %
  const peak = (m === 11 && day >= 15) || (m === 0 && day <= 5) || m === 6 || m === 7;
  return peak ? 1.15 : 1.0;
};
// Suggestion de récompense ajustée à la catégorie, la ville et la période.
const dynamicReward = (cat, place, date) => {
  const base = REWARD_SUGGEST[cat] ?? 15;
  const val = base * cityMultiplier(place) * periodMultiplier(date);
  return Math.max(5, Math.round(val / 5) * 5);
};
const isAdjusted = (place, date) => cityMultiplier(place) > 1 || periodMultiplier(date) > 1;

export default function DeclareLost() {
  const { t } = useLang();
  const { user, refreshUser } = useB2C();
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);
  const [consent, setConsent] = useState(() => new URLSearchParams(window.location.search).get("payment_cancelled") === "1");
  const [created, setCreated] = useState(null);
  const [boostOpen, setBoostOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editorSrc, setEditorSrc] = useState(null);
  const [editorTarget, setEditorTarget] = useState("image_base64");
  const [pub, setPub] = useState({ plan: "30d", boost: null });
  const [step, setStep] = useState(() => { const q = new URLSearchParams(window.location.search); return q.get("payment_cancelled") === "1" && q.get("resume_draft") === "1" ? 2 : 1; });
  const formTopRef = useRef(null);
  const goStep = (n) => { setStep(n); setTimeout(() => formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 30); };
  const wizardTopRef = useRef(null);
  // Just signed in at the wizard's account step → bring the publication step into view.
  useEffect(() => { if (user && step === 2) setTimeout(() => wizardTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 420); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onRelaunch = () => goStep(2);
    window.addEventListener("findloop:relaunch-payment", onRelaunch);
    return () => window.removeEventListener("findloop:relaunch-payment", onRelaunch);
  }, []);
  const [form, setForm] = useState({
    category: CATEGORIES[0].key, description: "", lost_date: "", lost_place: "",
    country: "FR", image_base64: null, image_base64_2: null,
    owner_name: user?.name || "", owner_email: user?.email || "", owner_phone: user?.phone || "",
    ...EXTRA_DEFAULTS,
  });
  usePrefillFromProfile(user, setForm);
  const draft = useDraft("lost_ad", form, setForm, { enabled: !!user, label: form.description });
  const localDraft = useLocalWizardDraft("lost_ad", { form, setForm, step, setStep });
  useEffect(() => { setForm((f) => (JSON.stringify(f._pub) === JSON.stringify(pub) ? f : { ...f, _pub: pub })); }, [pub]);
  useEffect(() => { if (form._pub && JSON.stringify(form._pub) !== JSON.stringify(pub)) setPub(form._pub); }, [form._pub]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onFile(e, slot = "image_base64") {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const compressed = await fileToCompressedDataUrl(f, { maxDim: 2000, quality: 0.9 });
      setEditorTarget(slot);
      setEditorSrc(compressed);
    } catch (err) {
      toast.error(t("pub.cantProcessImage"));
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (step === 1) { goStep(2); return; }
    if (!consent) { toast.error(t("consent.required")); return; }
    if (phoneMissing(user, form, "post")) { toast.error(t("acc.phoneRequired")); return; }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      const idf = { ...form, ...accountIdentity(user, form) };
      await savePhoneIfMissing(user, idf.owner_phone, refreshUser);
      const res = await api("/public-ads", {
        method: "POST", auth: false,
        body: { ...idf, ...extraPayload(idf), _countryTouched: undefined, _pub: undefined, owner_email: idf.owner_email.trim().toLowerCase(), share_consent: true, plan: pub.plan, boost_tier: pub.boost, origin_url: window.location.origin, return_path: draft.resumePath, captcha_token: await getRecaptchaToken("report_lost") },
      });
      if (res?.url) { await draft.saveNow(); localDraft.clear(); toast.message(t("fa.redirectStripe")); goToCheckout(res.url, { returnTo: draft.resumePath }); return; }
      draft.clear();
      localDraft.clear();
      setCreated(res);
      toast.success(res?.free_publication ? t("fa.africaPublished") : t("an.published"));
    } catch (err) { toast.error(err.message); } finally { setBusy(false); submittingRef.current = false; }
  }

  const loggedIn = !!user;

  // Wizard: 1) declaration (no account needed) → 2) login / sign-up if needed → 3) publication.
  const wizardLabels = [t("form.stepDetails"), t("form.stepAccount"), t("form.stepPublish")];
  const view = created ? "done" : step === 1 ? "details" : !user ? "auth" : "publish";

  return (
    <div className="bg-background">
      <div className="relative h-64 sm:h-72 overflow-hidden photo-scrim">
        <motion.img
          initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          src={IMAGES.heroLost} alt=""
          className="w-full h-full object-cover object-[center_30%] animate-ken-burns" data-testid="declare-lost-banner" />
        <div className="absolute inset-0 bg-[#191A54]/45" aria-hidden="true" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6">
          <motion.span
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full badge-red px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">{t("an.badge")}</motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }}
            className="mt-4 font-display text-4xl md:text-5xl font-semibold">{t("an.postTitle")}</motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }}
            className="mt-2 text-white/85 max-w-lg">{t("an.postSubtitle")}</motion.p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div ref={wizardTopRef} className="scroll-mt-24" />
        <WizardViews view={view}>
        {created ? (
          <div className="bg-white rounded-2xl border border-border card-shadow p-8 text-center" data-testid="ad-created">
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-teal-100 text-teal-700"><CheckCircle2 size={34} /></div>
            <h2 className="mt-5 font-display text-2xl font-semibold text-findo-navy">{t("an.published")}</h2>
            <div className="mt-5 rounded-2xl bg-findo-red/5 border border-findo-red/30 p-5">
              <p className="inline-flex items-center gap-2 font-semibold text-findo-red"><Sparkles size={18} /> {t("bo.title")}</p>
              <p className="mt-1.5 text-sm text-slate-600">{t("an.boostPrompt")}</p>
              {loggedIn ? (
                <div className="mt-4 flex gap-2 justify-center">
                  <Button onClick={() => setBoostOpen(true)} className="rounded-full font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="post-boost-now">
                    <Sparkles size={15} className="mr-1.5" /> {t("an.boostNow")}
                  </Button>
                  <Link to="/annonces"><Button variant="ghost" className="rounded-full font-semibold" data-testid="post-boost-later">{t("an.boostLater")}</Button></Link>
                </div>
              ) : (
                <div className="mt-4">
                  <p className="text-sm text-slate-500 mb-2">{t("an.loginToBoost")}</p>
                  <Link to={`/mon-compte?renew=${created.id}`}><Button className="rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="post-boost-login">{t("an.goAccount")}</Button></Link>
                </div>
              )}
            </div>
            <ShareableAd adId={created.id} />
            <BoostAdDialog adId={created.id} open={boostOpen} onClose={() => setBoostOpen(false)} country={created.country} />
          </div>
        ) : view === "auth" ? (
          <WizardAuthStep t={t} title={t("an.lostLoginRequired")} subtitle={t("an.lostLoginDesc")} labels={wizardLabels} onBack={() => goStep(1)} />
        ) : (
          <form ref={formTopRef} onSubmit={submit} className="findo-form form-card bg-white rounded-3xl border border-border overflow-hidden scroll-mt-24" data-testid="post-ad-form">
            <SubmitOverlay show={busy} payment={!isAfrican(form.country)} />
            <FormHeader icon={SearchX} title={t("an.postTitle")} subtitle={t("an.postSubtitle")} testid="post-form-header">
              <StepProgress step={step === 1 ? 1 : 3} total={3} labels={wizardLabels} t={t} />
            </FormHeader>
            <div className="findo-form-body p-6 sm:p-8 space-y-6">
            {step === 1 ? (<>
            <div><Label>{t("dec.category")} <span className="text-findo-red">*</span></Label>
              <div className="mt-1.5">
                <CategorySelect value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="post-category" />
              </div>
            </div>
            <div><Label>{t("dec.description")} <span className="text-findo-red">*</span></Label><Textarea required minLength={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={(() => { const l = t("dec.descPhLong"); return l === "dec.descPhLong" ? t("hc.describeTheItemBrandColor") : l; })()} className="mt-1.5 rounded-xl" data-testid="post-description" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t("an.date")} <span className="text-findo-red">*</span></Label><Input type="date" required value={form.lost_date} onChange={(e) => setForm({ ...form, lost_date: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="post-date" /></div>
              <div><Label>{t("an.lostAt")} <span className="text-findo-red">*</span></Label><div className="mt-1.5"><CityAutocomplete value={form.lost_place} onChange={(v) => setForm({ ...form, lost_place: v })} country={form.country} placeholder={t("hc.parisGareDeLyon")} className="h-11 rounded-xl" testid="post-place" required /></div></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("mc.country")} <span className="text-findo-red">*</span></Label>
                <div className="mt-1.5">
                  <CountrySelect value={form.country} onChange={(v) => setForm({ ...form, country: v, _countryTouched: true })} testid="post-country" />
                </div>
              </div>
            </div>
            <ItemDetailFields form={form} setForm={setForm} prefix="post" />
            <div>
              <Label>{t("an.photo")}</Label>
              <label className="mt-1.5 flex items-center gap-2 h-11 px-3 rounded-xl border border-dashed border-border cursor-pointer text-sm text-slate-500 hover:border-findo-navy" data-testid="post-photo-label">
                <Upload size={16} /> {form.image_base64 ? t("pub.photoAdded") : t("an.photo")}
                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="post-photo" />
              </label>
              {form.image_base64 && (
                <motion.img
                  initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
                  src={form.image_base64} alt={t("pub.previewAlt")}
                  className="mt-2 w-full h-48 object-contain bg-slate-50 rounded-xl border border-border" data-testid="post-photo-preview" />
              )}
              {form.image_base64 && (
                <div className="mt-3" data-testid="post-photo2-block">
                  <label className="flex items-center gap-2 h-11 px-3 rounded-xl border border-dashed border-border cursor-pointer text-sm text-slate-500 hover:border-findo-navy" data-testid="post-photo2-label">
                    <Upload size={16} /> {form.image_base64_2 ? t("pub.photo2Added") : t("pub.addSecondPhoto")}
                    <input type="file" accept="image/*" onChange={(e) => onFile(e, "image_base64_2")} className="hidden" data-testid="post-photo2" />
                  </label>
                  {form.image_base64_2 && (
                    <div className="relative mt-2">
                      <img src={form.image_base64_2} alt={t("pub.previewAlt")}
                        className="w-full h-40 object-cover rounded-xl border border-border" data-testid="post-photo2-preview" />
                      <button type="button" onClick={() => setForm((s) => ({ ...s, image_base64_2: null }))}
                        className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80 text-lg leading-none" data-testid="post-photo2-remove">×</button>
                    </div>
                  )}
                </div>
              )}
            </div>


            <PreviousAdPrefill user={user} category={form.category} setForm={setForm} />
            <Button type="submit" className="w-full h-12 rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="post-next-step">
              {t("form.next")} <ArrowRight size={17} className="ml-1.5" />
            </Button>
            </>) : (<>
            <PublicationPlanPicker plan={pub.plan} boost={pub.boost} onChange={setPub} country={form.country} />
            <PublishedAs user={user} testid="post-published-as" />
            <MissingPhoneField user={user} form={form} setForm={setForm} prefix="post" />
            <label className="flex items-start gap-3 rounded-xl border border-border bg-slate-50 p-3.5 cursor-pointer" data-testid="consent-wrap">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required
                className="mt-0.5 h-4 w-4 shrink-0 accent-findo-navy" data-testid="consent-checkbox" />
              <span className="text-xs text-slate-600 leading-relaxed">{t("consent.share")}</span>
            </label>
            <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)} className="w-full h-11 rounded-xl font-semibold border-findo-navy text-findo-navy hover:bg-findo-navy/5" data-testid="post-preview-btn">
              <Eye size={16} className="mr-1.5" /> {t("pub.adPreviewBtn")}
            </Button>
            <Button type="submit" disabled={busy || !consent} className="w-full h-12 rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="post-submit">
              {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("an.publishing")}</span> : isAfrican(form.country) ? t("an.publish") : t("fa.payPublish")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => goStep(1)} className="w-full h-11 rounded-xl font-semibold text-findo-navy" data-testid="post-prev-step">
              <ArrowLeft size={16} className="mr-1.5" /> {t("form.back")}
            </Button>
            <AdPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} form={form} onPublish={() => { setPreviewOpen(false); submit({ preventDefault() {} }); }} busy={busy} />
            </>)}
            <RecaptchaNotice className="mt-3" />
            </div>
          </form>
        )}
        </WizardViews>
      </div>
      <PhotoEditor src={editorSrc} open={!!editorSrc}
        onClose={() => setEditorSrc(null)}
        onSave={async (b64) => { const safe = await securePhoto(b64, "ad", t); if (safe) setForm((s) => ({ ...s, [editorTarget]: safe })); }} />
    </div>
  );
}

function PreviousAdPrefill({ user, category, setForm }) {
  const { t } = useLang();
  const [ads, setAds] = useState([]);
  React.useEffect(() => { if (user) api("/b2c/my-ads", { b2c: true }).then((r) => setAds(Array.isArray(r) ? r : [])).catch(() => {}); }, [user]);
  const prev = ads.find((a) => a.category === category);
  if (!prev) return null;
  const fill = () => setForm((f) => ({
    ...f, description: prev.description || f.description, lost_place: prev.lost_place || f.lost_place,
    city: prev.city || f.city, circumstances: prev.circumstances || f.circumstances, color: prev.color || f.color,
    condition: prev.condition || f.condition, brand: prev.brand || f.brand, quantity: prev.quantity || f.quantity,
    country: prev.country || f.country, _countryTouched: true,
  }));
  return (
    <button type="button" onClick={fill} className="w-full rounded-xl border border-dashed border-findo-navy/30 px-3.5 py-2.5 text-left text-sm text-findo-navy hover:bg-findo-navy/5" data-testid="prefill-previous-ad">
      {t("fa.prefillPrevious")} <span className="text-slate-500">« {(prev.description || "").slice(0, 40)} »</span>
    </button>
  );
}

function ShareableAd({ adId }) {
  const { t } = useLang();
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/s/${adId}`;
  const imgUrl = `${API}/public-ads/${adId}/share-image.png`;
  const enc = encodeURIComponent;
  const text = t("hc.shareLostText");
  const links = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(text)}`,
    whatsapp: `https://wa.me/?text=${enc(text + " " + shareUrl)}`,
  };
  const open = (href) => window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  async function copy() {
    try { await navigator.clipboard.writeText(shareUrl); toast.success(t("pub.linkCopied")); }
    catch { toast.error(t("pub.copyFailed")); }
  }
  async function shareSocial(label) {
    if (navigator.share) {
      try { await navigator.share({ title: "findloop", text, url: shareUrl }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(shareUrl); toast.success(t("hc.linkCopiedShareOn").replace("{label}", label)); }
    catch { toast.error(t("pub.copyFailed")); }
  }
  const btn = "h-10 w-10 grid place-items-center rounded-full border border-border text-slate-500 hover:text-findo-navy hover:border-findo-navy transition-colors";
  return (
    <div className="mt-5 text-left" data-testid="shareable-ad">
      <p className="text-sm font-semibold text-findo-navy text-center">{t("pub.shareYourAd")}</p>
      <p className="text-xs text-slate-500 text-center mt-0.5">{t("pub.spreadNetwork")}</p>
      <img src={imgUrl} alt={t("pub.sharePreview")}
        className="mt-3 w-full rounded-xl border border-border" data-testid="shareable-ad-image" />
      <div className="mt-3 flex items-center justify-center gap-2">
        <button type="button" onClick={() => open(links.facebook)} className={btn} style={{ color: "#1877F2" }} title="Facebook" data-testid="shareable-facebook"><Facebook size={17} /></button>
        <button type="button" onClick={() => open(links.twitter)} className={btn} style={{ color: "#000000" }} title="X / Twitter" data-testid="shareable-twitter"><XIcon size={17} /></button>
        <button type="button" onClick={() => open(links.whatsapp)} className={btn} style={{ color: "#25D366" }} title="WhatsApp" data-testid="shareable-whatsapp"><MessageCircle size={17} /></button>
        <button type="button" onClick={() => shareSocial("Instagram")} className={btn} title="Instagram" data-testid="shareable-instagram"><InstagramIcon size={17} /></button>
        <button type="button" onClick={() => shareSocial("TikTok")} className={btn} title="TikTok" data-testid="shareable-tiktok"><TikTokIcon size={17} /></button>
        <button type="button" onClick={copy} className={btn} title={t("an.copyLink")} data-testid="shareable-copy"><Link2 size={17} /></button>
        <a href={imgUrl} download={`annonce-${adId}.png`} className={btn} title={t("pub.downloadImage")} data-testid="shareable-download"><Download size={17} /></a>
      </div>
    </div>
  );
}

function AdPreviewDialog({ open, onClose, form, onPublish, busy }) {
  const { t } = useLang();
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ad-preview-dialog">
        <DialogHeader>
          <DialogTitle icon={Eye}>{t("pub.yourAdPreview")}</DialogTitle>
          <DialogDescription>{t("pub.adAppearPublic")}</DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl border border-border overflow-hidden">
          {form.image_base64 ? (
            <img src={form.image_base64} alt={t("pub.previewAlt")} className="w-full h-44 object-cover" data-testid="preview-photo" />
          ) : (
            <div className="w-full h-44 bg-slate-100 grid place-items-center text-slate-400 text-sm">{t("pub.noPhoto")}</div>
          )}
          <div className="p-4 space-y-2">
            <span className="inline-flex items-center rounded-full bg-findo-navy/10 text-findo-navy px-2.5 py-1 text-xs font-bold" data-testid="preview-category">{labelFor(form.category)}</span>
            <p className="text-sm text-slate-700" data-testid="preview-description">{form.description || "—"}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 pt-1">
              <span className="inline-flex items-center gap-1" data-testid="preview-place"><MapPin size={13} /> {form.city || "—"} · {countryFlag(form.country)} {countryName(form.country)}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1 rounded-full font-semibold" data-testid="preview-edit-btn">{t("pub.edit")}</Button>
          <Button onClick={onPublish} disabled={busy} className="flex-1 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="preview-publish-btn">
            {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("pub.publishingWord")}</span> : isAfrican(form.country) ? t("an.publish") : t("fa.payPublish")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
